import { useState } from 'react';
import { supabase } from '../supabaseClient';
import { CURRENCIES, LC_TYPES, HEDGE_STATUSES } from '../utils/importFinancingUtils';

const USANCE_PERIODS = [30, 60, 90, 120, 150, 180];
const USANCE_FROM    = ['Bill of Lading', 'Sight of Documents', 'Port of Discharge'];

const BLANK = {
  lc_ref: '', lc_type: 'Sight', beneficiary: '', issuing_bank: '',
  currency: 'USD', amount_fcy: '', issue_date: '', maturity_date: '',
  usance_period_days: 90, usance_from: 'Bill of Lading',
  commission_rate: '', issuance_fee_inr: '', swift_fee_inr: '',
  amendment_fee_inr: '', negotiation_fee_inr: '', other_fee_inr: '',
  hedge_status: 'Unhedged', hedged_amount_fcy: '', contracted_rate_inr: '', spot_rate_inr: '',
};

export default function NewLCModal({ onClose, onSaved, companyId }) {
  const [form, setForm]   = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isUsance = form.lc_type === 'Usance';

  // Compute effective payment date for display
  const effectivePayDate = (() => {
    if (!isUsance || !form.maturity_date || !form.usance_period_days) return null;
    const d = new Date(form.maturity_date);
    d.setDate(d.getDate() + parseInt(form.usance_period_days));
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  })();

  async function handleSave() {
    if (!form.lc_ref || !form.beneficiary || !form.amount_fcy || !form.issue_date || !form.maturity_date || !form.commission_rate || !form.issuance_fee_inr) {
      setError('Please fill all required fields: LC Ref, Beneficiary, Amount, Issue Date, Maturity Date, Commission Rate, Issuance Fee.');
      return;
    }
    setSaving(true); setError('');
    const { error: err } = await supabase.from('import_financing').insert({
      company_id:          companyId,
      lc_ref:              form.lc_ref,
      lc_type:             form.lc_type,
      beneficiary:         form.beneficiary,
      issuing_bank:        form.issuing_bank,
      currency:            form.currency,
      amount_fcy:          parseFloat(form.amount_fcy),
      issue_date:          form.issue_date,
      maturity_date:       form.maturity_date,
      usance_period_days:  isUsance ? parseInt(form.usance_period_days) : null,
      usance_from:         isUsance ? form.usance_from : null,
      commission_rate:     parseFloat(form.commission_rate),
      issuance_fee_inr:    parseFloat(form.issuance_fee_inr) || 0,
      swift_fee_inr:       parseFloat(form.swift_fee_inr) || 0,
      amendment_fee_inr:   parseFloat(form.amendment_fee_inr) || 0,
      negotiation_fee_inr: parseFloat(form.negotiation_fee_inr) || 0,
      other_fee_inr:       parseFloat(form.other_fee_inr) || 0,
      hedge_status:        form.hedge_status,
      hedged_amount_fcy:   parseFloat(form.hedged_amount_fcy) || 0,
      contracted_rate_inr: parseFloat(form.contracted_rate_inr) || null,
      spot_rate_inr:       parseFloat(form.spot_rate_inr) || null,
      status:              'Active',
    });
    setSaving(false);
    if (err) { setError(err.message); return; }
    onSaved();
  }

  const Field = ({ label, k, type = 'text', placeholder = '', disabled = false }) => (
    <div>
      <label className="text-xs text-gray-400 block mb-1">{label}</label>
      <input type={type} value={form[k]} placeholder={placeholder} disabled={disabled}
        onChange={e => set(k, e.target.value)}
        className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white disabled:opacity-40" />
    </div>
  );

  const Select = ({ label, k, options }) => (
    <div>
      <label className="text-xs text-gray-400 block mb-1">{label}</label>
      <select value={form[k]} onChange={e => set(k, e.target.value)}
        className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white">
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-[560px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-white">New Letter of Credit</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-xl">✕</button>
        </div>

        {/* LC Details */}
        <div className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-800 pb-1 mb-3">LC Details</div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Field label="LC Reference *"  k="lc_ref"      placeholder="LC-2024-005" />
          <Select label="Type"           k="lc_type"     options={LC_TYPES} />
          <Field label="Beneficiary *"   k="beneficiary" placeholder="Supplier name" />
          <Field label="Issuing Bank"    k="issuing_bank" placeholder="e.g. HDFC Bank" />
          <Select label="Currency"       k="currency"    options={CURRENCIES} />
          <Field label="Amount (FCY) *"  k="amount_fcy"  type="number" placeholder="500000" />
          <Field label="Issue Date *"    k="issue_date"  type="date" />
          <Field label="Maturity Date *" k="maturity_date" type="date" />
        </div>

        {/* Usance fields — shown only when type = Usance */}
        {isUsance && (
          <>
            <div className="text-xs font-medium text-purple-400 uppercase tracking-wider border-b border-purple-900 pb-1 mb-3">
              Usance Terms
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs text-gray-400 block mb-1">Usance Period (days) *</label>
                <select value={form.usance_period_days} onChange={e => set('usance_period_days', e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-gray-800 border border-purple-800 rounded-lg text-white">
                  {USANCE_PERIODS.map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Usance Period From *</label>
                <select value={form.usance_from} onChange={e => set('usance_from', e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-gray-800 border border-purple-800 rounded-lg text-white">
                  {USANCE_FROM.map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
            </div>

            {/* Effective payment date callout */}
            {effectivePayDate && (
              <div className="flex items-center gap-3 px-4 py-2.5 bg-purple-900/30 border border-purple-800 rounded-lg mb-4 text-xs">
                <span className="text-purple-400">📅</span>
                <div>
                  <span className="text-gray-400">Effective Payment Date </span>
                  <span className="text-white font-medium">{effectivePayDate}</span>
                  <span className="text-gray-500 ml-2">(Maturity + {form.usance_period_days} days usance)</span>
                </div>
              </div>
            )}

            <div className="flex items-start gap-2 px-3 py-2.5 bg-gray-800 rounded-lg mb-4 text-xs text-gray-400">
              <span>💡</span>
              <div>
                Commission will be calculated on the full period including usance.
                When availng BC, tenor will default to <strong className="text-white">{form.usance_period_days} days</strong>.
              </div>
            </div>
          </>
        )}

        {/* Pricing */}
        <div className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-800 pb-1 mb-3">Pricing</div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Field label="Commission Rate (% p.a.) *" k="commission_rate"     type="number" placeholder="0.75" />
          <Field label="Issuance Fee (₹) *"         k="issuance_fee_inr"    type="number" placeholder="15000" />
          <Field label="SWIFT / Telex Charges (₹)"  k="swift_fee_inr"       type="number" placeholder="5000" />
          <Field label="Negotiation Fee (₹)"        k="negotiation_fee_inr" type="number" placeholder="8000" />
          <Field label="Amendment Fee (₹)"          k="amendment_fee_inr"   type="number" placeholder="0" />
          <Field label="Other Charges (₹)"          k="other_fee_inr"       type="number" placeholder="0" />
        </div>

        {/* Hedge */}
        <div className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-800 pb-1 mb-3">Hedge</div>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <Select label="Hedge Status" k="hedge_status" options={HEDGE_STATUSES} />
          <Field label="Hedged Amount (FCY)"     k="hedged_amount_fcy"   type="number" placeholder="0"     disabled={form.hedge_status === 'Unhedged'} />
          <Field label="Contracted Rate (INR)"   k="contracted_rate_inr" type="number" placeholder="83.50" disabled={form.hedge_status === 'Unhedged'} />
          
        </div>

        {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-700 text-gray-400 rounded-lg hover:text-gray-200">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-500 text-white rounded-lg disabled:opacity-50 transition-colors">
            {saving ? 'Saving...' : 'Save LC'}
          </button>
        </div>
      </div>
    </div>
  );
}
